<template>
  <Section
    :id="id"
    :header="t('common.valueOfGoods')"
    class="value-of-goods"
    data-class="flex flex-column flex-gap-1"
  >
    <RygenButton
      mode="tertiary"
      :label="displayValueOfGoods"
      :disabled="!editable"
      @click="showModal"
    >
      <template #icon>
        <FontAwesomeIcon
          aria-hidden="true"
          class="edit-icon p-button-icon p-button-icon-right"
          :icon="['fas', 'pencil']"
        />
      </template>
    </RygenButton>

    <ValueOfGoodsModal
      v-model:visible="valueOfGoodsModal.isVisible"
      v-model:value-of-goods="effectiveValueOfGoods"
    />
  </Section>
</template>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed, PropType, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Money } from '@rygen/rygen-ui/model';

import { useValueOfGoods } from '@/composables';
import { LineItem } from '@/model';

import ValueOfGoodsModal from '@/components/valueOfGoods/ValueOfGoodsModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const valueOfGoods = defineModel('valueOfGoods', {
  type: Object as PropType<Money | undefined>,
  default: () => undefined,
});

const lineItems = defineModel('lineItems', {
  type: Array as PropType<LineItem[]>,
  default: () => [],
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, editable } = toRefs(props);

const { t } = useI18n();

const valueOfGoodsModal = ref({
  isVisible: false,
});

const { effectiveValueOfGoods } = useValueOfGoods(valueOfGoods, lineItems);

const displayValueOfGoods = computed(() =>
  renderMoney(effectiveValueOfGoods.value),
);

const showModal = () => {
  if (editable.value) {
    valueOfGoodsModal.value.isVisible = true;
  }
};
</script>

<style>
.value-of-goods {
  --p-button-sm-padding-y: 0;
  --p-button-sm-padding-x: 0;
  --p-button-sm-font-size: 1rem;
  --p-button-text-primary-color: var(--p-surface-600);
  --p-button-text-primary-hover-background: transparent;
  --p-button-text-primary-active-background: transparent;
  --p-button-label-font-weight: 400;

  .rygen-button {
    &:hover {
      .edit-icon {
        opacity: 1;
      }
    }

    justify-content: space-between;

    .edit-icon {
      opacity: 0;
      transition: opacity 100ms ease-in;
    }
  }
}
</style>
